import React, { forwardRef, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Summary from "./Summary";
import Prescription from "../../Prescription/Prescription";
import Reference from "./Reference";
import Certificate from "../../../components/Certificate";

interface ChatMessageProps {
  message: string;
  timestamp: string;
  isDoctor: boolean;
  imageUrl?: string;
  fileName?: string;
  doctorName: string;
  doctorSpecialty: string;
  avatar?: string;
  onReply?: (msg: any) => void;
  messageID?: string;
  isHighlighted?: boolean;
  onReplyQuoteClick?: (messageID: string) => void;
  replyTo?: any;
  userDokter?: string;
  patientName?: string;
}

const highlightFade = keyframes`
  0% {
    background-color: rgba(172, 172, 172, 0.6);
    border-radius: 10px;
  }
  70% {
    background-color: rgba(218, 218, 215, 0.6);
    border-radius: 10px;
  }
  100% {
    background-color: transparent;
    border-radius: 10px;
  }
`;

const MessageContainer = styled.div<{ isDoctor?: boolean; translateX?: number; isHighlighted?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ isDoctor }) => (isDoctor ? 'row' : 'row-reverse')};
  margin: 4px 2px;
  position: relative;
  transition: transform 0.3s ease;
  transform: translateX(${({ translateX }) => translateX || 0}px);
  ${({ isHighlighted }) =>
  isHighlighted &&
  css`
    animation: ${highlightFade} 2s ease-out;
  `}
`;

const MessageWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin: 4px;
`;

const MessageBubble = styled.div<{ isDoctor?: boolean }>`
  position: relative;
  background-color: ${({ isDoctor }) => (isDoctor ? '#fff' : '#f1f1f1')};
  color: black;
  padding: 5px 10px;
  border-radius: ${({ isDoctor }) =>
    isDoctor ? '0px 12px 12px 12px' : '12px 0px 12px 12px'};
  max-width: 100%;
  word-break: break-word;
  box-shadow: 0px 1px 3px rgba(0,0,0,0.1);
`;

const Timestamp = styled.span`
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 4px; // Jarak dari konten pesan
  text-align: right;
`;

const ReplyQuote = styled.div<{ clickable?: boolean }>`
  padding: 6px;
  margin-bottom: 6px;
  border-radius: 2px;
  font-size: 12px;
  color: #666;
  max-width: 250px;
  background-color: #e0e0e0ff;
  border-radius: 10px;
  border-left: 3px solid #ed1c24;
  ${({ clickable }) => clickable && `
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #c9c9c9ff;
    }
  `}
`;

const QuoteSender = styled.div`
  font-weight: 600;
  color: #333;
`;

const QuoteMessage = styled.div`
  margin-top: 2px;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SystemBubble = styled.div`
  background-color: #f4c1c1ff;
  color: #000000ff;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  display: inline-block;
  margin: 10px auto;
`;

const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(({
  message,
  timestamp,
  isDoctor,
  imageUrl,
  fileName,
  doctorName,
  doctorSpecialty,
  avatar,
  onReply,
  messageID,
  isHighlighted,
  onReplyQuoteClick,
  replyTo: replyToProp,
  userDokter,
  patientName,
}, ref) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const SWIPE_THRESHOLD = 80;
  const MAX_TRANSLATE = 60;
  
  let parsedMessage: any;

  try {
    parsedMessage = JSON.parse(message);
  } catch {
    parsedMessage = message;
  }

  const isObjectMessage = typeof parsedMessage === "object" && parsedMessage !== null;
  const replyTo = replyToProp || parsedMessage?.replyTo;

  const resolvedImageUrl =
    imageUrl || parsedMessage?.Origin?.Url || parsedMessage?.url;

  const resolvedFileName =
    fileName || parsedMessage?.FileName || parsedMessage?.fileName;

  // ✅ SYSTEM MESSAGE DETECTION
  if (typeof message === "string" && (message.includes("accepted|") || message.includes("end|"))) {
    const label = message.includes("accepted|") ? "Konsultasi dimulai" : "Konsultasi berakhir";
    return (
      <div style={{ textAlign: "center" }}>
        <SystemBubble>{`${label} ${timestamp}`}</SystemBubble>
      </div>
    );
  }

  if (parsedMessage?.message === 'end') {
    return null;
  }

  if (parsedMessage?.message === 'start') {
    return null;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;

    if (diff > 0) {
      const translate = Math.min(diff, MAX_TRANSLATE);
      setTranslateX(translate);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const currentX = e.changedTouches[0].clientX;
    const diff = currentX - startX;

    if (diff > SWIPE_THRESHOLD) {
      onReply?.({
        messageID,
        message: parsedMessage?.message || message,
        isDoctor,
      });
    }
    setTranslateX(0);
    setIsDragging(false);
  };

  const handleTouchCancel = () => {
    setTranslateX(0);
    setIsDragging(false);
  };

  const getTruncatedMessage = (msg: string | any): string => {
    if (!msg) return '';
  
    const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
    const maxChars = 100;
    const maxLines = 2;
    
    const lines = msgStr.split('\n');
    
    if (lines.length > maxLines) {
      return lines.slice(0, maxLines).join('\n') + '...';
    }
    
    if (msg.length > maxChars) {
      return msg.substring(0, maxChars) + '...';
    }
    
    return msg;
  };

  const getSenderMessageText = (): string => {
    if (!replyTo) return '';

    try {
      if (replyTo.senderUserID === userDokter) {
        return doctorName;
      } else {
        return patientName || 'Pasien';
      }
    } catch (e) {
      return 'Pengguna';
    }
  };

  const getReplyMessageText = (): string => {
    if (!replyTo) return '';
    
    try {
      // ✅ PERBAIKAN: Format Flutter - value_reply adalah pesan yang di-reply
      const messageContent = replyTo.value_reply || replyTo.message || '';
      return getTruncatedMessage(messageContent);
    } catch (e) {
      return getTruncatedMessage(replyTo.message || '');
    }
  };


  return (
    <MessageContainer 
      ref={ref || messageRef}
      isDoctor={isDoctor}
      translateX={translateX}
      isHighlighted={isHighlighted}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      style={{ userSelect: 'none', touchAction: 'pan-y' }}
    >
      <MessageWrapper>
        {isDoctor && (
          <Avatar
            src={avatar} // Ganti dengan avatar dokter atau ambil dari props
            alt="Doctor"
          />
        )}
        <div>
          <MessageBubble isDoctor={isDoctor}>
            {replyTo && (
              <ReplyQuote
                clickable={true}
                onClick={() => onReplyQuoteClick?.(replyTo.messageID)}
              >
                <QuoteSender>{getSenderMessageText()}</QuoteSender>
                <QuoteMessage>{getReplyMessageText()}</QuoteMessage>
              </ReplyQuote>
            )}

            {resolvedImageUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(resolvedFileName || "") ? (
              <a href={resolvedImageUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={resolvedImageUrl}
                  alt={resolvedFileName || "Image"}
                  style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "8px" }}
                />
              </a>
            ) : resolvedImageUrl && /\.pdf$/i.test(resolvedFileName || "") ? (
              <div
                onClick={() => window.open(resolvedImageUrl, "_blank")}
                style={{
                  width: "180px",
                  height: "150px",
                  borderRadius: "10px",
                  overflow: "hidden",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  backgroundColor: "#f9f9f9",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "calc(100% + 20px)",
                    height: "calc(100% + 20px)",
                    position: "absolute",
                    top: "-10px",
                    left: "-10px",
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src={`${resolvedImageUrl}#toolbar=0&navpanes=0&zoom=60`}
                    title="PDF Preview"
                    style={{
                      width: "120%",
                      height: "120%",
                      border: "none",
                      pointerEvents: "none",
                    }}
                  />
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "rgba(0,0,0,0.6)",
                    color: "white",
                    fontSize: "12px",
                    padding: "4px 6px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {resolvedFileName?.replace(".pdf", "") || "Dokumen PDF"}
                </div>
              </div>
            ) : resolvedImageUrl ? (
              <a href={resolvedImageUrl} target="_blank" rel="noopener noreferrer">
                {resolvedFileName || "Download file"}
              </a>
            ) : isObjectMessage ? (
              parsedMessage?.type === "new-summary" ? (
                parsedMessage["type-summary"] === "soap" ? (
                  <Summary summary={parsedMessage.summary} doctorName={doctorName} doctorSpecialty={doctorSpecialty} />
                ) : parsedMessage["type-summary"] === "prescription" ? (
                  <Prescription summary={parsedMessage.summary} />
                ) : parsedMessage["type-summary"] === "reference" ? (
                  <Reference summary={parsedMessage.summary} />
                ) : parsedMessage["type-summary"] === "certificate" ? (
                  <Certificate summary={parsedMessage.summary} />
                ) : null
              ) : (
                <div>{parsedMessage?.message}</div>
              )
            ) : (
              <span>{typeof parsedMessage === "object" ? parsedMessage?.message : parsedMessage}</span>
            )}

            <Timestamp>{timestamp}</Timestamp>
          </MessageBubble>
        </div>
      </MessageWrapper>
    </MessageContainer>
  );
});
export default ChatMessage;
